import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  getMediaMimeType,
  isMediaImage,
  isMediaVideo,
} from '../utils/mediaDetect';

const SWIPE_THRESHOLD = 50;

function mediaTypeOf(item) {
  if (typeof item === 'string') return undefined;
  return item?.media_type || item?.type;
}

function itemUrl(item) {
  return typeof item === 'string' ? item : item?.url;
}

/**
 * Full-screen media viewer with swipe / arrow navigation for multi-photo posts.
 * Supports legacy `src` (single image) or `items` + `initialIndex` (gallery).
 */
export default function PhotoLightbox({
  src,
  items,
  initialIndex = 0,
  onClose,
}) {
  const gallery = Array.isArray(items) && items.length > 0
    ? items.filter((item) => itemUrl(item))
    : src
      ? [src]
      : [];

  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, gallery.length - 1))
  );
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const [dragX, setDragX] = useState(0);
  const [animating, setAnimating] = useState(false);
  const dragging = useRef(false);

  useEffect(() => {
    setIndex(Math.min(Math.max(0, initialIndex), Math.max(0, gallery.length - 1)));
  }, [initialIndex, gallery.length, src]);

  const goNext = useCallback(() => {
    setIndex((prev) => {
      if (gallery.length <= 1) return prev;
      return (prev + 1) % gallery.length;
    });
    setDragX(0);
    setAnimating(true);
    window.setTimeout(() => setAnimating(false), 200);
  }, [gallery.length]);

  const goPrev = useCallback(() => {
    setIndex((prev) => {
      if (gallery.length <= 1) return prev;
      return (prev - 1 + gallery.length) % gallery.length;
    });
    setDragX(0);
    setAnimating(true);
    window.setTimeout(() => setAnimating(false), 200);
  }, [gallery.length]);

  useEffect(() => {
    if (gallery.length === 0) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [gallery.length, onClose, goNext, goPrev]);

  if (gallery.length === 0) return null;

  const current = gallery[index];
  const url = itemUrl(current);
  const mime = mediaTypeOf(current);
  const isImg = isMediaImage(url, mime);
  const isVid = isMediaVideo(url, mime);
  const multi = gallery.length > 1;

  const beginDrag = (clientX) => {
    if (!multi) return;
    touchStartX.current = clientX;
    touchDeltaX.current = 0;
    dragging.current = true;
    setAnimating(false);
  };

  const moveDrag = (clientX) => {
    if (!multi || touchStartX.current == null || !dragging.current) return;
    const dx = clientX - touchStartX.current;
    touchDeltaX.current = dx;
    setDragX(dx);
  };

  const endDrag = () => {
    if (!multi || !dragging.current) return;
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    dragging.current = false;
    if (dx <= -SWIPE_THRESHOLD) {
      setDragX(0);
      goNext();
    } else if (dx >= SWIPE_THRESHOLD) {
      setDragX(0);
      goPrev();
    } else {
      setAnimating(true);
      setDragX(0);
      window.setTimeout(() => setAnimating(false), 200);
    }
  };

  const onTouchStart = (e) => beginDrag(e.touches[0].clientX);
  const onTouchMove = (e) => moveDrag(e.touches[0].clientX);
  const onTouchEnd = () => endDrag();

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    beginDrag(e.clientX);
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    moveDrag(e.clientX);
  };
  const onMouseUp = () => endDrag();
  const onMouseLeave = () => {
    if (dragging.current) endDrag();
  };

  const navBtnStyle = (side) => ({
    position: 'absolute',
    top: '50%',
    [side]: 12,
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.92)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        cursor: 'default',
        touchAction: 'pan-y',
        overflow: 'hidden',
      }}
    >
      {/* Close */}
      <button
        type="button"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 40,
          height: 40,
          padding: 0,
          fontSize: 22,
          lineHeight: 1,
          background: 'rgba(0,0,0,0.55)',
          color: '#fff',
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>

      {multi && (
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 0.3,
            zIndex: 3,
            background: 'rgba(0,0,0,0.4)',
            padding: '4px 12px',
            borderRadius: 999,
            pointerEvents: 'none',
          }}
        >
          {index + 1} / {gallery.length}
        </div>
      )}

      {multi && (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            style={navBtnStyle('left')}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            style={navBtnStyle('right')}
          >
            ›
          </button>
        </>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        style={{
          position: 'relative',
          maxWidth: '95vw',
          maxHeight: '90vh',
          width: '100%',
          height: '90vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `translateX(${dragX}px)`,
          transition: animating ? 'transform 0.2s ease' : 'none',
          cursor: multi ? (dragging.current ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
          zIndex: 2,
        }}
      >
        {isImg ? (
          <img
            src={url}
            alt={`Photo ${index + 1}`}
            draggable={false}
            style={{
              maxWidth: '92vw',
              maxHeight: '88vh',
              borderRadius: 8,
              boxShadow: '0 0 24px rgba(0, 0, 0, 0.45)',
              display: 'block',
              backgroundColor: '#111',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
          />
        ) : isVid ? (
          <video
            key={url}
            controls
            playsInline
            autoPlay
            controlsList="nodownload"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            style={{
              maxWidth: '92vw',
              maxHeight: '88vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 8,
              background: '#000',
              display: 'block',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <source src={url} type={getMediaMimeType(url, mime)} />
          </video>
        ) : (
          <img
            src={url}
            alt={`Media ${index + 1}`}
            draggable={false}
            style={{
              maxWidth: '92vw',
              maxHeight: '88vh',
              borderRadius: 8,
              display: 'block',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    </div>,
    document.body
  );
}
